/**
 * The lead page (/leads/:leadId): the lead modal laid out as a page. Opened
 * from links that leave the list, such as the comm log's "View lead" (in a new
 * tab) and Cancel on the convert page. The header (LeadDetailFrame's, the
 * same one the modal shows) stays fixed at the top; only the body scrolls.
 */
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { EmptyState } from '@/components/ui';
import { LeadDetailFrame } from './LeadDetailModal';

export default function LeadDetailPage() {
  const { leadId } = useParams<{ leadId: string }>();
  const navigate = useNavigate();
  const numericId = Number(leadId);

  if (!leadId || Number.isNaN(numericId)) {
    return (
      <EmptyState
        title="Lead not found"
        description="This lead could not be loaded because the ID is invalid."
      />
    );
  }

  return (
    <LeadDetailFrame key={numericId} leadId={numericId} mode="page" onClose={() => navigate('/leads')}>
      {({ header, body }) => (
        <div className="flex h-full min-h-0 flex-col overflow-x-hidden bg-white max-md:h-auto">
          {/* The modal's header bar: same padding and rule, with Back where the modal has its X. */}
          <div className="flex shrink-0 items-start gap-3 border-b border-slate-200 px-4 py-4 md:px-6">
            <Link
              to="/leads"
              aria-label="Back to Leads"
              className="mt-0.5 shrink-0 rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
            >
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div className="min-w-0 flex-1">
              {header ?? <h1 className="text-base font-semibold text-slate-900">Lead Details</h1>}
            </div>
          </div>
          <div className="relative min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-4 py-5 md:px-6">
            {body}
          </div>
        </div>
      )}
    </LeadDetailFrame>
  );
}
